/**
 * iter301 — Paramètres → Comptabilité → TVA
 *
 * Méthode de décompte et historique des taux de dette fiscale nette (TDFN).
 * Règle non négociable : un nouveau taux a une DATE D'EFFET et ne réécrit jamais
 * les périodes antérieures. Les périodes couvertes par un décompte clôturé sont
 * verrouillées côté serveur.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Percent, Pencil, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtDate = (d) => (d ? d.split("-").reverse().join(".") : "—");

const TauxDialog = ({ ouvert, taux, onClose, onSaved }) => {
  const edition = !!taux;
  const [valeur, setValeur] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!ouvert) return;
    setValeur(taux ? String(taux.taux) : "");
    setDateDebut(taux ? taux.date_debut : new Date().toISOString().slice(0, 10));
    setNote(taux?.note || "");
  }, [ouvert, taux]);

  const submit = async () => {
    const v = parseFloat(String(valeur).replace(",", "."));
    if (Number.isNaN(v) || v < 0 || v > 100) { toast.error("Taux invalide"); return; }
    if (edition && !window.confirm(
      `Modifier la période qui démarre le ${fmtDate(taux.date_debut)} ?\n\n`
      + "Les décomptes déjà clôturés ne sont pas touchés, mais les décomptes ouverts "
      + "de cette période seront recalculés.",
    )) return;
    setBusy(true);
    try {
      if (edition) {
        await api.patch(`/tva/taux/${taux.id}`, { taux: v, date_debut: dateDebut, note, confirmer: true });
      } else {
        await api.post("/tva/taux", { taux: v, date_debut: dateDebut, note });
      }
      toast.success(`Taux ${v} % applicable dès le ${fmtDate(dateDebut)}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{edition ? "Modifier une période de taux" : "Nouveau taux TDFN"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Taux de la dette fiscale nette (%) *</Label>
            <Input value={valeur} onChange={(e) => setValeur(e.target.value)} placeholder="3.20" data-testid="tdfn-taux-valeur" />
          </div>
          <div>
            <Label>Applicable dès le *</Label>
            <Input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} data-testid="tdfn-taux-date" />
            <p className="text-[11px] text-gray-500 mt-1">
              La période précédente se termine automatiquement la veille. Les opérations
              antérieures gardent leur taux d&apos;origine.
            </p>
          </div>
          <div>
            <Label>Note (facultatif)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Décision AFC du ..." data-testid="tdfn-taux-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="tdfn-taux-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TvaMethodeCard = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ ouvert: false, taux: null });

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tva/config");
      setCfg(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chargement impossible");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const changerMethode = async (methode) => {
    if (methode === cfg?.methode) return;
    // iter328 — Un changement de méthode est daté : les périodes antérieures gardent
    // la leur et ne sont jamais recalculées.
    const defaut = new Date().toISOString().slice(0, 10);
    const saisie = window.prompt(
      `Date d'entrée en vigueur de la méthode ${methode === "tdfn" ? "TDFN" : "effective"} ?\n\n`
      + "Les opérations antérieures gardent leur régime actuel.",
      defaut,
    );
    if (!saisie) return;
    try {
      await api.put("/tva/config", { methode, date_debut: saisie.trim() });
      toast.success(
        `${methode === "tdfn" ? "Méthode TDFN" : "Méthode effective"} applicable dès le ${fmtDate(saisie.trim())}`,
      );
      charger();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const supprimer = async (t) => {
    if (!window.confirm(`Supprimer la période du ${fmtDate(t.date_debut)} (${t.taux} %) ?`)) return;
    try {
      await api.delete(`/tva/taux/${t.id}?confirmer=true`);
      toast.success("Période supprimée");
      charger();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <Card className="p-4"><Loader2 className="w-4 h-4 animate-spin" /></Card>;

  const tdfn = cfg?.methode === "tdfn";

  return (
    <Card className="p-4 sm:p-5" data-testid="tva-methode-card">
      <div className="flex items-center gap-2 mb-1">
        <Percent className="w-4 h-4 text-[#52a675]" />
        <h3 className="text-base font-semibold text-gray-900">TVA — méthode de décompte</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Les factures continuent d&apos;afficher et de comptabiliser leur TVA normale (8,1 % / 2,6 %).
        Le taux ci-dessous ne sert qu&apos;à calculer ce que tu verses à l&apos;AFC.
      </p>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { v: "tdfn", lib: "Taux de la dette fiscale nette (TDFN)" },
          { v: "effective", lib: "Méthode effective" },
        ].map((m) => (
          <button
            key={m.v}
            type="button"
            onClick={() => changerMethode(m.v)}
            className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
              cfg?.methode === m.v
                ? "border-[#52a675] bg-[#52a675]/10 text-[#2d5f3f]"
                : "border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
            data-testid={`tva-methode-${m.v}`}
          >
            {m.lib}
          </button>
        ))}
      </div>

      {tdfn && (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
              Historique des taux
            </div>
            <Button size="sm" variant="outline" onClick={() => setDialog({ ouvert: true, taux: null })} data-testid="tdfn-ajouter-taux">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter un taux
            </Button>
          </div>

          {(cfg.taux || []).length === 0 ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="tdfn-aucun-taux">
              Aucun taux enregistré : le décompte ne calculera rien tant que tu n&apos;auras pas
              saisi un taux avec sa date d&apos;effet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="pb-1 font-medium">Dès le</th>
                  <th className="pb-1 font-medium">Jusqu&apos;au</th>
                  <th className="pb-1 font-medium text-right">Taux</th>
                  <th className="pb-1 font-medium hidden sm:table-cell">Note</th>
                  <th className="pb-1"></th>
                </tr>
              </thead>
              <tbody>
                {cfg.taux.map((t) => {
                  const courant = cfg.taux_courant?.id === t.id;
                  return (
                    <tr key={t.id} className={`border-t border-gray-100 ${courant ? "bg-[#52a675]/5" : ""}`} data-testid={`tdfn-taux-${t.id}`}>
                      <td className="py-1.5 font-mono text-xs">{fmtDate(t.date_debut)}</td>
                      <td className="py-1.5 font-mono text-xs text-gray-500">
                        {t.date_fin ? fmtDate(t.date_fin) : "en cours"}
                      </td>
                      <td className="py-1.5 text-right font-mono font-semibold">{t.taux.toFixed(2)} %</td>
                      <td className="py-1.5 text-xs text-gray-500 truncate max-w-[160px] hidden sm:table-cell">{t.note || "—"}</td>
                      <td className="py-1.5 text-right whitespace-nowrap">
                        <button type="button" className="p-1 text-gray-400 hover:text-gray-700" onClick={() => setDialog({ ouvert: true, taux: t })} data-testid={`tdfn-modifier-${t.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" className="p-1 text-gray-400 hover:text-red-600" onClick={() => supprimer(t)} data-testid={`tdfn-supprimer-${t.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div className="mt-3 flex items-start gap-1.5 text-[11px] text-gray-500">
            <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            Une période déjà couverte par un décompte clôturé ne peut plus changer de taux :
            il faut d&apos;abord rouvrir le décompte concerné.
          </div>
        </>
      )}

      {/* iter328 — Historique des régimes : chaque méthode a sa date d'effet */}
      {(cfg?.regimes || []).length > 1 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1.5">
            Historique des régimes
          </div>
          <ul className="space-y-1" data-testid="tva-regimes-historique">
            {cfg.regimes.map((r) => (
              <li key={r.id} className="text-xs text-gray-600 font-mono" data-testid={`tva-regime-${r.id}`}>
                {fmtDate(r.date_debut)} → {r.date_fin ? fmtDate(r.date_fin) : "en cours"} ·{" "}
                {r.methode === "tdfn" ? "TDFN" : "effective"}
              </li>
            ))}
          </ul>
        </div>
      )}

      <TauxDialog
        ouvert={dialog.ouvert}
        taux={dialog.taux}
        onClose={() => setDialog({ ouvert: false, taux: null })}
        onSaved={() => { setDialog({ ouvert: false, taux: null }); charger(); }}
      />
    </Card>
  );
};

export default TvaMethodeCard;
